import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../../contexts/AuthContext';
import { useToast } from '../../Toast';

const API_URL = '/api';

/**
 * AdminDashboard component - Displays admin statistics and user activity
 * Requires admin privileges to access
 */
function AdminDashboard() {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const { logout, darkMode, toggleDarkMode } = React.useContext(AuthContext);
  const toast = useToast();

  // Email modal states
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailModalUser, setEmailModalUser] = useState(null);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');

  // Bulk email modal states
  const [showBulkEmailModal, setShowBulkEmailModal] = useState(false);
  const [bulkEmailStep, setBulkEmailStep] = useState(1); // 1: Compose, 2: Preview, 3: Confirm
  const [bulkEmailFilters, setBulkEmailFilters] = useState({ verified_only: false, active_only: false });
  const [bulkEmailPreview, setBulkEmailPreview] = useState(null);
  const [confirmationToken, setConfirmationToken] = useState(null);

  useEffect(() => {
    loadAdminData();
  }, []);

  const loadAdminData = async () => {
    try {
      const [usersResponse, statsResponse] = await Promise.all([
        axios.get(`${API_URL}/admin/users`),
        axios.get(`${API_URL}/admin/stats`)
      ]);
      setUsers(usersResponse.data);
      setStats(statsResponse.data);
      setLoading(false);
    } catch (err) {
      console.error('Failed to load admin data', err);
      if (err.response?.status === 403) {
        toast.error('Access denied. Admin privileges required.');
      } else {
        toast.error('Failed to load admin data');
      }
      setLoading(false);
    }
  };

  const handleSuspendToggle = async (userId, currentStatus) => {
    const action = currentStatus ? 'suspend' : 'activate';
    if (!window.confirm(`Are you sure you want to ${action} this user account?`)) {
      return;
    }

    setActionLoading(`suspend-${userId}`);
    try {
      const response = await axios.put(`${API_URL}/admin/users/${userId}/suspend`);
      toast.success(response.data.message);
      await loadAdminData();
    } catch (err) {
      console.error('Failed to toggle suspension', err);
      toast.error(err.response?.data?.error || 'Failed to update user status');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteUser = async (userId, username) => {
    const confirmText = username;
    const userInput = prompt(
      `⚠️ WARNING: This action is IRREVERSIBLE!\n\n` +
      `You are about to delete user "${username}" and ALL their data:\n` +
      `• All their projects\n` +
      `• All their references and annotations\n` +
      `• All their categories\n` +
      `• Their collaboration data\n\n` +
      `Type "${confirmText}" to confirm deletion:`
    );

    if (userInput !== confirmText) {
      if (userInput !== null) {
        toast.error('Account deletion cancelled');
      }
      return;
    }

    const reason = prompt('Optional: Provide a reason for deletion (will be sent to user via email):');

    setActionLoading(`delete-${userId}`);
    try {
      await axios.delete(`${API_URL}/admin/users/${userId}`, {
        data: { reason: reason || '' }
      });
      toast.success(`User ${username} deleted successfully`);
      await loadAdminData();
    } catch (err) {
      console.error('Failed to delete user', err);
      toast.error(err.response?.data?.error || 'Failed to delete user');
    } finally {
      setActionLoading(null);
    }
  };

  const openEmailModal = (user) => {
    setEmailModalUser(user);
    setEmailSubject('');
    setEmailMessage('');
    setShowEmailModal(true);
  };

  const closeEmailModal = () => {
    setShowEmailModal(false);
    setEmailModalUser(null);
    setEmailSubject('');
    setEmailMessage('');
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();

    if (!emailSubject.trim() || !emailMessage.trim()) {
      toast.error('Subject and message are required');
      return;
    }

    setActionLoading('send-email');
    try {
      await axios.post(`${API_URL}/admin/email/user/${emailModalUser.id}`, {
        subject: emailSubject,
        message: emailMessage
      });
      toast.success(`Email sent to ${emailModalUser.username} successfully`);
      closeEmailModal();
    } catch (err) {
      console.error('Failed to send email', err);
      toast.error(err.response?.data?.error || 'Failed to send email');
    } finally {
      setActionLoading(null);
    }
  };

  const openBulkEmailModal = () => {
    setShowBulkEmailModal(true);
    setBulkEmailStep(1);
    setEmailSubject('');
    setEmailMessage('');
    setBulkEmailFilters({ verified_only: false, active_only: false });
    setBulkEmailPreview(null);
    setConfirmationToken(null);
  };

  const closeBulkEmailModal = () => {
    setShowBulkEmailModal(false);
    setBulkEmailStep(1);
    setEmailSubject('');
    setEmailMessage('');
    setBulkEmailFilters({ verified_only: false, active_only: false });
    setBulkEmailPreview(null);
    setConfirmationToken(null);
  };

  const handleBulkEmailPreview = async () => {
    if (!emailSubject.trim() || !emailMessage.trim()) {
      toast.error('Subject and message are required');
      return;
    }

    setActionLoading('bulk-preview');
    try {
      const response = await axios.post(`${API_URL}/admin/email/all`, {
        subject: emailSubject,
        message: emailMessage,
        filters: bulkEmailFilters
      });
      setBulkEmailPreview(response.data);
      setConfirmationToken(response.data.confirmation_token);
      setBulkEmailStep(2);
    } catch (err) {
      console.error('Failed to generate preview', err);
      toast.error(err.response?.data?.error || 'Failed to generate preview');
    } finally {
      setActionLoading(null);
    }
  };

  const handleBulkEmailSend = async () => {
    setActionLoading('bulk-send');
    try {
      const response = await axios.post(`${API_URL}/admin/email/all/confirm`, {
        confirmation_token: confirmationToken
      });
      toast.success(`Bulk email sent to ${response.data.sent_count} users`);
      if (response.data.failed_count > 0) {
        toast.error(`Failed to send to ${response.data.failed_count} users`);
      }
      closeBulkEmailModal();
    } catch (err) {
      console.error('Failed to send bulk email', err);
      toast.error(err.response?.data?.error || 'Failed to send bulk email');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return <div className="container"><h2>Loading...</h2></div>;
  }

  return (
    <div className="container admin-container">
      <div className="header">
        <h1>Admin Dashboard</h1>
        <div className="header-actions">
          <Link to="/" className="back-link-btn">Back to Projects</Link>
          <button onClick={toggleDarkMode} className="theme-toggle" title="Toggle dark mode">
            {darkMode ? '☀️' : '🕶️'}
          </button>
          <button onClick={logout} className="logout-btn">Logout</button>
        </div>
      </div>

      {stats && (
        <div className="admin-stats">
          <div className="stat-card">
            <h3>Total Users</h3>
            <p className="stat-number">{stats.total_users}</p>
          </div>
          <div className="stat-card">
            <h3>Verified Users</h3>
            <p className="stat-number">{stats.verified_users || 0}</p>
          </div>
          <div className="stat-card">
            <h3>Unverified Users</h3>
            <p className="stat-number">{stats.unverified_users || 0}</p>
          </div>
          <div className="stat-card">
            <h3>Total Projects</h3>
            <p className="stat-number">{stats.total_projects}</p>
          </div>
          <div className="stat-card">
            <h3>Total References</h3>
            <p className="stat-number">{stats.total_references}</p>
          </div>
          <div className="stat-card">
            <h3>Active Collaborations</h3>
            <p className="stat-number">{stats.total_collaborations}</p>
          </div>
        </div>
      )}

      <div className="admin-section">
        <div className="admin-section-header">
          <h2>User Activity</h2>
          <button onClick={openBulkEmailModal} className="btn-primary">
            📧 Email All Users
          </button>
        </div>
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Username</th>
                <th>Email</th>
                <th>Status</th>
                <th>Verified</th>
                <th>Projects</th>
                <th>Collabs</th>
                <th>Refs</th>
                <th>Joined</th>
                <th>Last Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id}>
                  <td>{user.id}</td>
                  <td>{user.username}</td>
                  <td>{user.email}</td>
                  <td>
                    <span className={`status-badge ${user.is_active ? 'active' : 'suspended'}`}>
                      {user.is_active ? 'Active' : '⊘ Suspended'}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${user.email_verified ? 'verified' : 'unverified'}`}>
                      {user.email_verified ? '✓' : '✗'}
                    </span>
                  </td>
                  <td>{user.projects_owned}</td>
                  <td>{user.collaborations}</td>
                  <td>{user.references_added}</td>
                  <td>{new Date(user.created_at).toLocaleDateString()}</td>
                  <td>
                    {user.last_active
                      ? new Date(user.last_active).toLocaleString()
                      : 'Never'}
                  </td>
                  <td>
                    <div className="admin-actions">
                      <button
                        onClick={() => handleSuspendToggle(user.id, user.is_active)}
                        className={`btn-sm ${user.is_active ? 'btn-warning' : 'btn-success'}`}
                        disabled={actionLoading === `suspend-${user.id}`}
                        title={user.is_active ? 'Suspend account' : 'Activate account'}
                      >
                        {actionLoading === `suspend-${user.id}` ? '...' : (user.is_active ? '⊘' : '✓')}
                      </button>
                      <button
                        onClick={() => openEmailModal(user)}
                        className="btn-sm btn-secondary"
                        title="Send email"
                      >
                        ✉️
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.id, user.username)}
                        className="btn-sm btn-danger"
                        disabled={actionLoading === `delete-${user.id}`}
                        title="Delete account"
                      >
                        {actionLoading === `delete-${user.id}` ? '...' : '🗑️'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Email Single User Modal */}
      {showEmailModal && (
        <div className="modal-overlay" onClick={closeEmailModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Send Email to {emailModalUser?.username}</h2>
              <button onClick={closeEmailModal} className="modal-close">&times;</button>
            </div>
            <form onSubmit={handleSendEmail}>
              <div className="form-group">
                <label htmlFor="email-subject">Subject</label>
                <input
                  id="email-subject"
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Email subject"
                  required
                  disabled={actionLoading === 'send-email'}
                />
              </div>
              <div className="form-group">
                <label htmlFor="email-message">Message</label>
                <textarea
                  id="email-message"
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  placeholder="Your message..."
                  rows="8"
                  required
                  disabled={actionLoading === 'send-email'}
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={closeEmailModal} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={actionLoading === 'send-email'}>
                  {actionLoading === 'send-email' ? 'Sending...' : 'Send Email'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Email Modal */}
      {showBulkEmailModal && (
        <div className="modal-overlay" onClick={closeBulkEmailModal}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {bulkEmailStep === 1 && 'Compose Bulk Email'}
                {bulkEmailStep === 2 && 'Preview Bulk Email'}
                {bulkEmailStep === 3 && 'Confirm Bulk Email'}
              </h2>
              <button onClick={closeBulkEmailModal} className="modal-close">&times;</button>
            </div>

            {/* Step 1: Compose */}
            {bulkEmailStep === 1 && (
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="bulk-subject">Subject</label>
                  <input
                    id="bulk-subject"
                    type="text"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="Email subject"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="bulk-message">Message</label>
                  <textarea
                    id="bulk-message"
                    value={emailMessage}
                    onChange={(e) => setEmailMessage(e.target.value)}
                    placeholder="Your message..."
                    rows="8"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Filters</label>
                  <div className="checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={bulkEmailFilters.verified_only}
                        onChange={(e) => setBulkEmailFilters({ ...bulkEmailFilters, verified_only: e.target.checked })}
                      />
                      Verified users only
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={bulkEmailFilters.active_only}
                        onChange={(e) => setBulkEmailFilters({ ...bulkEmailFilters, active_only: e.target.checked })}
                      />
                      Active users only
                    </label>
                  </div>
                </div>
                <div className="modal-actions">
                  <button onClick={closeBulkEmailModal} className="btn-secondary">Cancel</button>
                  <button onClick={handleBulkEmailPreview} className="btn-primary" disabled={actionLoading === 'bulk-preview'}>
                    {actionLoading === 'bulk-preview' ? 'Loading...' : 'Next: Preview'}
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Preview */}
            {bulkEmailStep === 2 && bulkEmailPreview && (
              <div className="modal-body">
                <div className="preview-info">
                  <h3>Email Preview</h3>
                  <p><strong>Subject:</strong> {emailSubject}</p>
                  <p><strong>Message:</strong></p>
                  <div className="preview-message">{emailMessage}</div>
                  <p><strong>Recipients:</strong> {bulkEmailPreview.recipient_count} users</p>
                  <div className="sample-recipients">
                    <p><strong>Sample recipients:</strong></p>
                    <ul>
                      {bulkEmailPreview.sample_recipients.map((recipient, idx) => (
                        <li key={idx}>{recipient.username} ({recipient.email})</li>
                      ))}
                      {bulkEmailPreview.recipient_count > 5 && <li>...and {bulkEmailPreview.recipient_count - 5} more</li>}
                    </ul>
                  </div>
                </div>
                <div className="modal-actions">
                  <button onClick={() => setBulkEmailStep(1)} className="btn-secondary">Back</button>
                  <button onClick={() => setBulkEmailStep(3)} className="btn-primary">Next: Confirm</button>
                </div>
              </div>
            )}

            {/* Step 3: Confirm */}
            {bulkEmailStep === 3 && (
              <div className="modal-body">
                <div className="warning-banner">
                  <h3>⚠️ Final Confirmation Required</h3>
                  <p>You are about to send an email to <strong>{bulkEmailPreview?.recipient_count} users</strong>.</p>
                  <p>This action cannot be undone. Are you absolutely sure?</p>
                </div>
                <div className="modal-actions">
                  <button onClick={() => setBulkEmailStep(2)} className="btn-secondary">Back</button>
                  <button onClick={handleBulkEmailSend} className="btn-danger" disabled={actionLoading === 'bulk-send'}>
                    {actionLoading === 'bulk-send' ? 'Sending...' : 'Confirm and Send'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;
